// The era strip, enhanced. Without this file it is a row of same-page links
// that scrolls sideways, and every one of them works.
//
// With it:
// - the chip for the period being read is lit, and kept in view along the
//   strip, so the strip says where you are as well as where you could go;
// - the earlier/later buttons appear. Eighteen chips do not fit across even a
//   wide window, and a sideways row answers only a trackpad or a horizontal
//   wheel — the chronology chart taught that a mouse with an ordinary wheel
//   cannot move one at all.
//
// A scroll handler rather than an IntersectionObserver: "the period being
// read" is the last one whose heading has passed under the strip, which is a
// comparison of eighteen tops, not a question of what is on screen. A period
// with one entry can be shorter than any reading band.

function reducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

// The last shown period whose heading has passed `line`. At the foot of the
// page the last few periods can never scroll that far — there is no page left
// below them — so there it is the last one whose heading is on screen at all.
export function currentPeriod(sections, line, atFoot = false, viewportHeight = Infinity) {
  let current = null;
  for (const section of sections) {
    if (section.hidden) continue;
    const top = section.getBoundingClientRect().top;
    if (top <= line || (atFoot && top < viewportHeight)) current = section;
    else break;
  }
  return current;
}

export function enhance(strip) {
  if (strip.dataset.eraStripReady) return;
  strip.dataset.eraStripReady = "true";

  const track = strip.querySelector("[data-era-track]");
  const earlier = strip.querySelector("[data-era-earlier]");
  const later = strip.querySelector("[data-era-later]");
  const links = [...strip.querySelectorAll("[data-era-link]")];
  const sections = links
    .map((link) => document.getElementById(link.dataset.eraLink))
    .filter(Boolean);

  const syncSteps = () => {
    const overflowing = track.scrollWidth > track.clientWidth + 1;
    strip.dataset.overflowing = String(overflowing);
    earlier.hidden = !overflowing;
    later.hidden = !overflowing;
    earlier.disabled = track.scrollLeft <= 1;
    later.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    // A fade on whichever edge has more beyond it: the row says it goes on
    // before anybody reaches for a button.
    strip.dataset.moreEarlier = String(overflowing && !earlier.disabled);
    strip.dataset.moreLater = String(overflowing && !later.disabled);
  };

  const step = (direction) => {
    track.scrollBy({
      left: direction * track.clientWidth * 0.8,
      behavior: reducedMotion() ? "auto" : "smooth"
    });
  };
  earlier.addEventListener("click", () => step(-1));
  later.addEventListener("click", () => step(1));

  // Along the strip only. scrollIntoView would move the PAGE as well, and the
  // page is exactly what the reader is scrolling.
  //
  // It ALWAYS scrolls, even when the chip is already in view — to where the
  // strip already is. A new scroll cancels one in flight, and without that a
  // smooth scroll toward the PREVIOUS period (started by the page scrolling a
  // moment before) carried on after a click and slid the chosen chip out of
  // sight. On a phone that happened about one click in six.
  //
  // Positions are measured against the TRACK. offsetLeft is measured from the
  // chip's offset parent, which is the sticky strip — earlier button and all —
  // so it was 44px out, enough to centre a chip just off the edge of a phone.
  const reveal = (chip) => {
    const item = chip.closest("li");
    const left = item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    const right = left + item.offsetWidth;
    const inView = left >= track.scrollLeft && right <= track.scrollLeft + track.clientWidth;
    track.scrollTo(inView
      ? { left: track.scrollLeft, behavior: "auto" }
      : { left: left - (track.clientWidth - item.offsetWidth) / 2,
          behavior: reducedMotion() ? "auto" : "smooth" });
  };

  let lit = null;
  const light = (id) => {
    if (id === lit) return;
    lit = id;
    links.forEach((link) => {
      const here = link.dataset.eraLink === id;
      if (here) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
      if (here) reveal(link);
    });
  };

  // A chip that was just CHOSEN stays lit until the reader scrolls again.
  // Otherwise a short period near the foot, which the jump cannot bring up
  // under the strip, would hand the light straight to its neighbour.
  let chosen = null;

  let frame = null;
  const measure = () => {
    frame = null;
    if (chosen) {
      if (chosen.y === null) chosen.y = window.scrollY;
      if (Math.abs(window.scrollY - chosen.y) < 2) return;
      chosen = null;
    }
    // A period is being read once its heading has gone under the strip. The
    // jump leaves a heading scroll-margin below it, so the line sits lower.
    const line = strip.getBoundingClientRect().bottom + 24;
    const atFoot = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    light(currentPeriod(sections, line, atFoot, window.innerHeight)?.id ?? null);
  };
  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(measure);
  };

  // A chip lights the moment it is chosen, not a frame after the jump.
  links.forEach((link) => link.addEventListener("click", () => {
    chosen = { y: null };
    light(link.dataset.eraLink);
  }));

  // Turbo keeps the window alive across visits; its listeners must not
  // outlive the page that added them.
  const page = new AbortController();
  document.addEventListener("turbo:before-render", () => page.abort(), { once: true });

  track.addEventListener("scroll", syncSteps, { passive: true });
  window.addEventListener("scroll", schedule, { passive: true, signal: page.signal });
  window.addEventListener("resize", () => { syncSteps(); schedule(); }, { signal: page.signal });
  // Filtering hides chips and periods; the steps and the lit chip both follow.
  strip.addEventListener("era-strip:changed", () => { syncSteps(); schedule(); });

  syncSteps();
  measure();
}

export function start() {
  document.querySelectorAll("[data-era-strip]").forEach(enhance);
}

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
